"use client";

import { Action, useAnalytics } from "@/context/AnalyticsProvider";
import { usePassportProvider } from "@/context/PassportProvider";
import { cn } from "@/lib/utils";
import { notifyError } from "@/utils/monitoring";
import { HorizontalScroll } from "@components/HorizontalScroll";
import { Button } from "@components/ui/button";
import { useWishlistHandler } from "@hooks/useWishlistHandler";
import { Heart, Loader2 } from "lucide-react";
import Image from "next/image";
import { GemsBadge } from "../quest-badges";
import { marbleXGames } from "./data";
import { GameCard } from "./game-card";

const MarbleXLogo = () => {
  return (
    <div className="min-w-[12.5rem] sm:min-w-[15.625rem] lg:min-w-[19.1875rem] max-w-[12.5rem] sm:max-w-[15.625rem] lg:max-w-[19.1875rem] min-h-[2.5rem] sm:min-h-[3.125rem] lg:min-h-[3.75rem] max-h-[2.5rem] sm:max-h-[3.125rem] lg:max-h-[3.75rem] relative">
      <Image
        src="/images/marblex-games/marble-x-logo.webp"
        alt="Marble X Logo"
        fill
        className="object-contain"
      />
    </div>
  );
};

const ImmutableLogo = () => {
  return (
    <div className="min-w-[2.5rem] sm:min-w-[3.125rem] lg:min-w-[3.75rem] max-w-[2.5rem] sm:max-w-[3.125rem] lg:max-w-[3.75rem] min-h-[2.5rem] sm:min-h-[3.125rem] lg:min-h-[3.75rem] max-h-[2.5rem] sm:max-h-[3.125rem] lg:max-h-[3.75rem] relative">
      <Image
        src="/images/polygon-games/immutable-logo.webp"
        alt="Immutable"
        fill
        className="object-contain"
      />
    </div>
  );
};

const CrossoverIcon = () => {
  return (
    <div className="min-w-[1.5rem] max-w-[1.5rem] min-h-[1.5rem] max-h-[1.5rem] mx-5 relative">
      <Image
        src="/images/polygon-games/crossover-icon.webp"
        alt="Crossover"
        fill
        className="object-contain"
      />
    </div>
  );
};

const SocialStats = () => {
  const { trackV3 } = useAnalytics();
  const handleClick = () => {
    trackV3({
      object: "Button",
      action: Action.PRESSED,
      extras: {
        control: "FollowOnX",
        section: "MarbleXHubPage",
      },
    });
  };

  return (
    <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3 lg:gap-8 border-t border-b border-white/10 py-3 sm:py-4">
      <a
        className="flex items-center gap-2 whitespace-nowrap hover:opacity-90 transition"
        href="https://x.com/MARBLEXofficial"
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick}
      >
        <Image
          src="/images/polygon-games/x-logo.webp"
          alt="X (Twitter)"
          width={16}
          height={16}
          className="object-contain flex-shrink-0"
        />
        <p className="text-xs sm:text-sm text-white/80">@MARBLEXofficial</p>
        <p className="text-xs sm:text-sm text-white/80">158K Followers</p>
      </a>
    </div>
  );
};

const HeaderSection = () => {
  return (
    <div className="px-4 sm:px-6 lg:px-10 pt-4 sm:pt-6 lg:pt-8 flex flex-col gap-4 sm:gap-6 lg:gap-8">
      <div className="flex items-center">
        <ImmutableLogo />
        <CrossoverIcon />
        <MarbleXLogo />
      </div>
      <SocialStats />
    </div>
  );
};

const HeroContent = () => {
  return (
    <div className="space-y-3 sm:space-y-4">
      <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-[3rem] font-bold text-white leading-tight sm:leading-tight md:leading-tight lg:leading-[3rem]">
        Exclusive Marblex Games & Prizes
        <br />
        Jump into the Marblex universe!
      </h1>
      <p className="text-sm sm:text-base md:text-lg lg:text-[1.125rem] font-light text-white/80 leading-relaxed sm:leading-relaxed md:leading-relaxed lg:leading-[1.75rem] max-w-full lg:max-w-[56.5625rem]">
        Explore new worlds, play fresh IPs, and chase massive rewards daily
      </p>
    </div>
  );
};

const HeroCTAButton = () => {
  const { trackV3 } = useAnalytics();
  const { isLoggedIn } = usePassportProvider();

  const gameId = "049e040e-9c82-4ba1-bcd6-b635972530a5";

  const { addToWishlist, isWishlisting, isWishlisted } = useWishlistHandler(
    gameId,
    10, // TODO confirm how many gems?
  );

  const handleWishlistQuest = async () => {
    if (isWishlisted) return;

    try {
      trackV3({
        object: "Quest",
        action: Action.PRESSED,
        extras: {
          questName: "Follow Now",
          questType: "wishlist",
          gameId: gameId,
          section: "MarbleXHubPage",
          isLoggedIn: Boolean(isLoggedIn),
        },
      });

      await addToWishlist();
    } catch (error) {
      notifyError(
        new Error(`Failed to complete MarbleX wishlist quest: ${error}`),
        "wishlist",
      );
    }
  };

  return (
    <Button
      size="lg"
      className={cn(
        "h-10 sm:h-12 lg:h-14 px-4 sm:px-5 lg:px-6 rounded-xl shadow-lg w-full sm:w-auto bg-[#25F352] text-black hover:bg-[#25F352]/80",
        isWishlisted && "pointer-events-none",
      )}
      onClick={handleWishlistQuest}
      disabled={isWishlisting}
    >
      <div className="flex items-center gap-3 sm:gap-4 lg:gap-5">
        {isWishlisting ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <Heart className={cn("w-5 h-5", isWishlisted && "fill-current")} />
        )}
        <p className="text-sm sm:text-base lg:text-lg font-medium">
          {isWishlisted ? "Following" : "Follow Now"}
        </p>
        {!isWishlisted && (
          <div className="flex items-center scale-125 px-1">
            <GemsBadge gems={10} />
          </div>
        )}
      </div>
    </Button>
  );
};

const HeroSection = () => {
  return (
    <div className="px-4 sm:px-6 lg:px-10 pt-6 sm:pt-8 lg:pt-10 transition-all duration-200 ease-out flex-1 space-y-6 sm:space-y-8 lg:space-y-10">
      <HeroContent />
      <HeroCTAButton />
    </div>
  );
};

const SectionHeader = ({ title }: { title: string }) => {
  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="h-px bg-white/20" />
      <h2 className="text-xl sm:text-2xl lg:text-[1.875rem] font-bold text-white">
        {title}
      </h2>
    </div>
  );
};

const GamesCarousel = () => {
  return (
    <div className="w-full overflow-hidden relative [&>div>button]:!z-20">
      <HorizontalScroll>
        {marbleXGames.map((game) => (
          <GameCard key={game.id} game={game} />
        ))}
      </HorizontalScroll>
    </div>
  );
};

const GamesSection = () => {
  return (
    <div className="pl-4 sm:pl-6 lg:pl-10 py-6 sm:py-8 space-y-4 sm:space-y-6">
      <SectionHeader title="New drops. Fresh quests. Endless fun. Only on Marblex" />
      <GamesCarousel />
    </div>
  );
};

export function MarbleXPageContent() {
  return (
    <div className="min-h-screen text-white overflow-x-hidden relative flex-1 min-w-0">
      <HeaderSection />
      <HeroSection />
      <GamesSection />
    </div>
  );
}
